"use client";

import { Dialog, DialogContent } from "@cmp-src/primitives/Dialog";
import {
  UpdateWorkplaceRequestParams,
  WorkplaceRequestElement,
  WorkplaceRequestFieldValueInput,
  WorkplaceRequestTypeElement
} from "@gettactic/api";
import { IconTile } from "@gettactic/components/src/data-display/IconTile";
import { HookFormUnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput";
import { type IconValue } from "@gettactic/components/src/icons";
import { Button } from "@gettactic/components/src/primitives/button";
import { isSubmittableFieldType } from "@gettactic/helpers/src/workplace-requests/fieldTraits";
import { reservationFieldConfig } from "@gettactic/helpers/src/workplace-requests/reservationField";
import { toWorkplaceRequestFieldValue } from "@gettactic/helpers/src/workplace-requests/toWorkplaceRequestFieldValue";
import { toWorkplaceRequestFormValue } from "@gettactic/helpers/src/workplace-requests/toWorkplaceRequestFormValue";
import { useAuthenticated, useOfficesQuery } from "@gettactic/hooks";
import { X } from "lucide-react";
import React, { useEffect, useMemo } from "react";
import type { FieldValues } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useWorkplaceRequestMutations } from "@/features/workplace-requests/hooks/useWorkplaceRequestMutations";
import { DynamicFieldInput } from "@/features/workplace-requests/views/queue/steps/DynamicFieldInput";

const parseIconValue = (value?: string | null): IconValue | null => {
  if (!value) return null;
  const separator = value.indexOf(":");
  if (separator === -1) return null;
  return { set: value.slice(0, separator), icon: value.slice(separator + 1) };
};

interface EditRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: WorkplaceRequestElement;
  type?: WorkplaceRequestTypeElement;
  onSaved?: (updates: UpdateWorkplaceRequestParams) => void;
}

export const EditRequestModal: React.FC<EditRequestModalProps> = ({
  isOpen,
  onClose,
  request,
  type,
  onSaved
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const viewerName = authenticatedUser.user?.name || "you";
  const officesQuery = useOfficesQuery();
  const { updateWorkplaceRequest, updateWorkplaceRequestMutation } =
    useWorkplaceRequestMutations();

  const fields = type?.fields ?? [];

  const offices = useMemo(() => {
    const all = (officesQuery.data?.offices ?? []).map((o) => ({
      id: o.id,
      name: o.name
    }));
    if (type?.has_office_restrictions && type.office_ids?.length) {
      const allowed = new Set(type.office_ids);
      return all.filter((o) => allowed.has(o.id));
    }
    return all;
  }, [officesQuery.data, type?.has_office_restrictions, type?.office_ids]);

  const form = useForm<FieldValues>({ defaultValues: {} });

  // Seed the form from the request when the modal opens, and re-seed once the
  // type finishes loading so dynamic fields get their saved values even when the
  // modal opened before `type` was available.
  useEffect(() => {
    if (!isOpen) return;
    const valueByFieldId = new Map(
      (request.field_values ?? []).map((fv) => [fv.id, fv.value])
    );
    const defaults: FieldValues = { office_id: request.office_id };
    for (const field of fields) {
      // Info blocks are rendered read-only in the list below, but they hold no
      // value: seeding one would put a stray key in the form state.
      if (!isSubmittableFieldType(field.field_type)) continue;
      const value = toWorkplaceRequestFormValue(
        field.field_type,
        valueByFieldId.get(field.id)
      );
      defaults[field.id] =
        value ??
        (field.field_type === "toggle"
          ? false
          : field.field_type === "multi_select"
            ? []
            : "");
    }
    form.reset(defaults);
  }, [isOpen, request.id, type?.id]);

  const handleSave = form.handleSubmit(async (values) => {
    const dirty = form.formState.dirtyFields;
    const fieldValues: WorkplaceRequestFieldValueInput[] = [];
    for (const field of fields) {
      // The backend rejects the whole update if an info block turns up here.
      if (!isSubmittableFieldType(field.field_type)) continue;
      if (!dirty[field.id]) continue;
      let value = toWorkplaceRequestFieldValue(
        field.field_type,
        values[field.id],
        reservationFieldConfig(field)
      );
      if (field.field_type === "number") {
        value = value === "" || value == null ? null : Number(value);
      }
      fieldValues.push({ field_id: field.id, value });
    }

    const updates: UpdateWorkplaceRequestParams = {};
    if (fieldValues.length > 0) updates.field_values = fieldValues;
    if (dirty.office_id) updates.office_id = values.office_id;

    if (Object.keys(updates).length > 0) {
      try {
        await updateWorkplaceRequest(request.id, updates);
      } catch {
        // The mutation surfaces its own error toast; keep the modal open so
        // the user does not believe the edit was saved.
        return;
      }
      onSaved?.(updates);
    }
    onClose();
  });

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="flex max-h-[90vh] max-w-2xl flex-col gap-0 p-0"
        // biome-ignore lint/complexity/noUselessFragments: hide the default close button
        customCloseButton={<></>}
      >
        <div className="flex items-start justify-between border-neutral-100 border-b p-5">
          <div>
            <h2 className="font-semibold text-lg text-neutral-900">
              Edit request #{request.display_id}
            </h2>
            <p className="text-neutral-700 text-sm">
              {request.type?.name} · Editing as {viewerName}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-md p-1 text-neutral-400 hover:text-neutral-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-5">
          <div className="flex items-start gap-3">
            <IconTile
              color="#FAFAFA"
              icon={parseIconValue(type?.icon)}
              iconColor="#404040"
              size="lg"
              bordered
            />
            <div>
              <div className="font-semibold text-neutral-900">
                {request.type?.name}
              </div>
              {type?.description ? (
                <p className="text-neutral-700 text-sm">{type.description}</p>
              ) : null}
            </div>
          </div>

          <HookFormUnifiedSelectInput
            form={form}
            name="office_id"
            label="Location"
            items={offices}
            placeholder="Select a location"
          />

          {fields.map((field) => (
            <DynamicFieldInput key={field.id} form={form} field={field} />
          ))}
        </div>

        <div className="flex flex-none items-center justify-between border-neutral-100 border-t p-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            isLoading={updateWorkplaceRequestMutation.isLoading}
          >
            Save changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
